import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Switch } from 'components/ui/switch';
import { USAGE_LIMITS_I18N } from 'features/usage-config/constants/usage-meters';
import { cn } from 'lib/utils';
import { useGetLlmModels } from 'features/usage-config/hooks/use-llm-models';
import {
  ILimitWindow,
  IModelCategory,
  IModelRestriction,
} from 'features/usage-config/types/usage-enforcement.model';

import { clampShare, shareOf, withWindowShare } from './limit-windows';
import { allowedByRestriction, groupModels } from './model-groups';
import { keyAsTyped, keyFromName, uniqueKey } from './usage-keys';
import { UsageKeyInput } from './usage-key-input';
import { UsageCardFrame, UsageFieldOrigin } from './usage-card-frame';

const T = USAGE_LIMITS_I18N;

export interface ModelCategoriesCardProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  categories: IModelCategory[];
  onCategoriesChange: (categories: IModelCategory[]) => void;
  windows: ILimitWindow[];
  /** While it is on, a category can hold only the models it picked. */
  restriction: IModelRestriction;
  organizationId: string;
  dimmed?: boolean;
  readOnly?: boolean;
  inherited?: boolean;
  onFollowTenant?: () => void;
  /** Why each category's key cannot be saved, by position; "" when it can. */
  keyErrors?: readonly string[];
}

export const ModelCategoriesCard = ({
  enabled,
  onEnabledChange,
  categories,
  onCategoriesChange,
  windows,
  restriction,
  organizationId,
  dimmed,
  readOnly,
  inherited,
  onFollowTenant,
  keyErrors = [],
}: ModelCategoriesCardProps) => {
  const { t } = useTranslation();
  const { data: models = [] } = useGetLlmModels();
  const [pickerIndex, setPickerIndex] = useState<number | null>(null);
  const [shareIndex, setShareIndex] = useState<number | null>(null);
  const enabledWindows = windows.filter((limitWindow) => limitWindow.enabled);
  // A custom model still being validated is not something a category may rest on. A
  // category names every model by its model name, so one name is offered once.
  const groups = useMemo(
    () =>
      groupModels(
        allowedByRestriction(models, restriction),
        (model) => model.is_active !== false && model.status === 'validated',
        t(T.MODELS_CUSTOM_EMPTY),
        t
      ).map((group) => ({
        ...group,
        models: [
          ...new Map(
            group.models.map((model) => [
              model.model_name,
              { id: model.model_name, label: model.model_name_label || model.model_name },
            ])
          ).values(),
        ],
      })),
    [models, restriction, t]
  );

  // Only what can be picked now is shown: a model deleted since, or one the restriction
  // leaves out, is dropped from the row and the picker, and from the category on its next
  // edit.
  const available = useMemo(
    () => new Map(groups.flatMap((group) => group.models.map((model) => [model.id, model.label]))),
    [groups]
  );
  const availableIn = (category: IModelCategory) =>
    category.models.filter((modelName) => available.has(modelName));
  const labelFor = (modelName: string) => available.get(modelName) ?? modelName;

  const commit = (index: number, next: IModelCategory) => {
    onCategoriesChange(categories.map((category, i) => (i === index ? next : category)));
  };

  const remove = (index: number) => {
    onCategoriesChange(categories.filter((_, i) => i !== index));
  };

  // Categories and windows share one namespace, so a new or derived key steers clear of both.
  const keysBesides = (index: number) => [
    ...categories.filter((_, other) => other !== index).map((category) => category.key),
    ...windows.map((limitWindow) => limitWindow.key),
  ];

  const add = () => {
    const name = t(T.CATEGORY_DEFAULT_NAME, { index: categories.length + 1 });
    onCategoriesChange([
      ...categories,
      { key: uniqueKey(keyFromName(name), keysBesides(-1)), name, models: [], window_share: {} },
    ]);
  };

  const onName = (index: number, name: string) => {
    const current = categories[index];
    const taken = keysBesides(index);
    const stillDerived = current.key === uniqueKey(keyFromName(current.name), taken);
    commit(index, {
      ...current,
      name,
      key: stillDerived ? uniqueKey(keyFromName(name), taken) : current.key,
    });
  };

  return (
    <UsageCardFrame cardId="categories" label={t(T.CATEGORIES_TITLE)} dimmed={dimmed}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <h3 className="text-sm font-semibold">{t(T.CATEGORIES_TITLE)}</h3>
          <p className="mt-1 max-w-prose text-sm leading-relaxed text-muted-foreground">
            {t(T.CATEGORIES_DESCRIPTION)}
          </p>
          <p className="mt-2 max-w-prose text-xs leading-relaxed text-muted-foreground">
            {t(T.TENANT_ONLY_NOTE)}
          </p>
        </div>
        <div className="flex items-center gap-3 sm:shrink-0">
          <UsageFieldOrigin
            inherited={inherited}
            onFollowTenant={onFollowTenant}
            readOnly={readOnly}
          />
          <Switch
            checked={enabled}
            onCheckedChange={onEnabledChange}
            disabled={readOnly}
            aria-label={t(T.CATEGORIES_LABEL)}
          />
        </div>
      </div>

      {!enabled && (
        <p className="rounded-sm bg-muted px-3 py-2 text-sm text-muted-foreground">
          {t(T.CATEGORIES_OFF_NOTE)}
        </p>
      )}

      {enabled && (
        <div className="flex flex-col gap-3">
          {restriction.enabled && (
            <p className="text-xs text-muted-foreground">{t(T.CATEGORIES_RESTRICTED_NOTE)}</p>
          )}
          <div className="flex justify-end">
            <Button type="button" variant="outline" size="sm" onClick={add} disabled={readOnly}>
              {t(T.CATEGORY_ADD)}
            </Button>
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[48rem]">
              <div className="flex items-center gap-3 border-b pb-2 text-xs text-muted-foreground">
                <span className="w-40 shrink-0">{t(T.CATEGORY_NAME)}</span>
                <span className="w-60 shrink-0">{t(T.CATEGORY_KEY)}</span>
                <span className="min-w-0 flex-1">{t(T.CATEGORY_MODELS)}</span>
                {enabledWindows.length > 0 && (
                  <span className="w-56 shrink-0">{t(T.WINDOW_LIMITS)}</span>
                )}
                <span className="w-8 shrink-0" />
              </div>

              {categories.map((category, index) => (
                <CategoryRow
                  key={index}
                  category={category}
                  models={availableIn(category)}
                  labelFor={labelFor}
                  windows={enabledWindows}
                  readOnly={readOnly}
                  onName={(name) => onName(index, name)}
                  keyError={keyErrors[index] ?? ''}
                  onKey={(key) => commit(index, { ...category, key: keyAsTyped(key) })}
                  onEditShares={() => setShareIndex(index)}
                  onPick={() => setPickerIndex(index)}
                  onRemove={() => remove(index)}
                />
              ))}
            </div>
          </div>

          {!categories.length && (
            <p className="rounded-sm bg-muted px-3 py-2 text-sm text-muted-foreground">
              {t(T.CATEGORIES_EMPTY)}
            </p>
          )}

          {categories.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {enabledWindows.length ? t(T.WINDOW_SHARE_NOTE) : t(T.WINDOW_SHARE_EMPTY)}
            </p>
          )}

          <div className="flex flex-col gap-2 border-t pt-3">
            <p className="max-w-prose text-xs text-muted-foreground">{t(T.GRANTS_NOTE)}</p>
            <p className="rounded-sm bg-muted px-3 py-2 text-sm text-muted-foreground">
              {t(T.GRANTS_RULE)}
            </p>
          </div>
        </div>
      )}

      {shareIndex !== null && categories[shareIndex] && (
        <WindowShareDialog
          category={categories[shareIndex]}
          windows={enabledWindows}
          readOnly={readOnly}
          onClose={() => setShareIndex(null)}
          onShare={(windowKey, share) =>
            commit(shareIndex, withWindowShare(categories[shareIndex], windowKey, share))
          }
        />
      )}

      {pickerIndex !== null && categories[pickerIndex] && (
        <ModelPickerDialog
          category={categories[pickerIndex]}
          chosen={availableIn(categories[pickerIndex])}
          categories={categories}
          groups={groups}
          onClose={() => setPickerIndex(null)}
          onApply={(selected) => {
            commit(pickerIndex, { ...categories[pickerIndex], models: selected });
            setPickerIndex(null);
          }}
        />
      )}
    </UsageCardFrame>
  );
};

interface CategoryRowProps {
  keyError: string;
  category: IModelCategory;
  /** The category's models that are still available. */
  models: string[];
  labelFor: (modelName: string) => string;
  windows: ILimitWindow[];
  readOnly?: boolean;
  onName: (name: string) => void;
  onKey: (key: string) => void;
  onEditShares: () => void;
  onPick: () => void;
  onRemove: () => void;
}

const CategoryRow = ({
  keyError,
  category,
  models,
  labelFor,
  windows,
  readOnly,
  onName,
  onKey,
  onEditShares,
  onPick,
  onRemove,
}: CategoryRowProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-3 border-b py-3">
      <Input
        className="h-8 w-40 shrink-0"
        value={category.name}
        disabled={readOnly}
        aria-label={t(T.CATEGORY_NAME)}
        onChange={(event) => onName(event.target.value)}
      />
      <UsageKeyInput
        className="w-60 shrink-0"
        value={category.key}
        readOnly={readOnly}
        label={t(T.CATEGORY_KEY)}
        copyLabel={t(T.CATEGORY_KEY_COPY)}
        hint={t(T.CATEGORY_KEY_HINT)}
        placeholder={t(T.CATEGORY_KEY_PLACEHOLDER)}
        error={keyError}
        onChange={onKey}
      />
      <div className="flex min-w-0 flex-1 items-center">
        {/* The overflow count opens the same editor, so a second button would be the same
            action twice. One control, whatever the count. */}
        {models.length ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 max-w-full gap-2 font-normal"
            aria-label={t(T.CATEGORY_EDIT_MODELS, { name: category.name || category.key })}
            disabled={readOnly}
            onClick={onPick}
          >
            <span className="truncate font-mono text-xs">{labelFor(models[0])}</span>
            {models.length > 1 && (
              <span className="shrink-0 text-xs text-muted-foreground">
                {t(T.CATEGORY_MORE, { count: models.length - 1 })}
              </span>
            )}
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8"
            onClick={onPick}
            disabled={readOnly}
          >
            {t(T.CATEGORY_PICK_MODELS)}
          </Button>
        )}
      </div>
      {windows.length > 0 && (
        <ShareSummary
          category={category}
          windows={windows}
          readOnly={readOnly}
          onEdit={onEditShares}
        />
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
        title={t(T.CATEGORY_REMOVE)}
        aria-label={t(T.CATEGORY_REMOVE)}
        onClick={onRemove}
        disabled={readOnly}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

interface ShareSummaryProps {
  category: IModelCategory;
  windows: ILimitWindow[];
  readOnly?: boolean;
  onEdit: () => void;
}

/**
 * The first window this category limits, then a count, the same shape as the models cell.
 * A window it left alone is no limit, so naming every one of them was noise.
 */
const ShareSummary = ({ category, windows, readOnly, onEdit }: ShareSummaryProps) => {
  const { t } = useTranslation();
  const limited = windows
    .map((limitWindow) => ({ limitWindow, share: shareOf(category.window_share, limitWindow.key) }))
    .filter((entry): entry is { limitWindow: ILimitWindow; share: number } => entry.share !== null);
  const [first, ...rest] = limited;

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-8 w-56 shrink-0 justify-start gap-2 font-normal hover:bg-muted"
      aria-label={t(T.WINDOW_SHARE_EDIT_FOR, { name: category.name || category.key })}
      disabled={readOnly}
      onClick={onEdit}
    >
      {first ? (
        <>
          <Badge
            variant="outline"
            className="min-w-0 gap-1 font-normal"
            title={t(T.WINDOW_SHARE_OF, { name: first.limitWindow.name, percent: first.share })}
          >
            <span className="truncate text-muted-foreground">{first.limitWindow.name}</span>
            <span className="shrink-0 font-medium text-foreground">
              {first.share}
              {t(T.WINDOW_SHARE_SUFFIX)}
            </span>
          </Badge>
          {rest.length > 0 && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {t(T.CATEGORY_MORE, { count: rest.length })}
            </span>
          )}
        </>
      ) : (
        <span className="text-xs text-muted-foreground">{t(T.WINDOW_SHARE_NONE_SET)}</span>
      )}
    </Button>
  );
};

interface WindowShareDialogProps {
  category: IModelCategory;
  windows: ILimitWindow[];
  readOnly?: boolean;
  onClose: () => void;
  onShare: (windowKey: string, share: number | null) => void;
}

const WindowShareDialog = ({
  category,
  windows,
  readOnly,
  onClose,
  onShare,
}: WindowShareDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t(T.WINDOW_SHARE_TITLE, { name: category.name || category.key })}</DialogTitle>
          <DialogDescription>{t(T.WINDOW_SHARE_NOTE)}</DialogDescription>
        </DialogHeader>
        {windows.length === 0 ? (
          <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
            {t(T.WINDOW_SHARE_EMPTY)}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {windows.map((limitWindow) => {
              const share = shareOf(category.window_share, limitWindow.key);
              const limited = share !== null;
              const daysKey =
                limitWindow.days_per_window === 1 ? T.WINDOW_LENGTH_ONE_DAY : T.WINDOW_LENGTH_DAILY;
              return (
                <li
                  key={limitWindow.key}
                  className={cn(
                    'flex items-center gap-3 rounded-md border px-3 py-2.5',
                    limited ? 'border-primary/40 bg-muted/50' : 'hover:bg-muted/40'
                  )}
                >
                  <Checkbox
                    id={`usage-window-share-${limitWindow.key}`}
                    checked={limited}
                    disabled={readOnly}
                    onCheckedChange={(checked) =>
                      onShare(limitWindow.key, checked === true ? 100 : null)
                    }
                  />
                  <label
                    htmlFor={`usage-window-share-${limitWindow.key}`}
                    className={cn(
                      'flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-3',
                      readOnly && 'cursor-default'
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{limitWindow.name}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {limitWindow.type === 'hourly'
                          ? t(T.WINDOW_LENGTH_HOURLY, { hours: limitWindow.hours })
                          : t(daysKey, { days: limitWindow.days_per_window })}
                      </span>
                    </span>
                    {share === null && (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {t(T.NO_WINDOW_LIMIT)}
                      </span>
                    )}
                  </label>
                  {share !== null && (
                    <span className="flex shrink-0 items-center gap-1.5">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={5}
                        className="h-8 w-16 text-right"
                        value={share}
                        disabled={readOnly}
                        aria-label={t(T.WINDOW_SHARE_PERCENT, { name: limitWindow.name })}
                        onChange={(event) =>
                          onShare(limitWindow.key, clampShare(Number(event.target.value)))
                        }
                      />
                      <span className="text-xs text-muted-foreground">{t(T.WINDOW_SHARE_SUFFIX)}</span>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {t(T.WINDOW_SHARE_DONE)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface ModelOptionGroup {
  key: string;
  label: string;
  emptyNote?: string;
  models: { id: string; label: string }[];
}

interface ModelPickerDialogProps {
  category: IModelCategory;
  /** What the category holds among the models on offer. */
  chosen: string[];
  categories: IModelCategory[];
  groups: ModelOptionGroup[];
  onClose: () => void;
  onApply: (models: string[]) => void;
}

const ModelPickerDialog = ({
  category,
  chosen,
  categories,
  groups,
  onClose,
  onApply,
}: ModelPickerDialogProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>(chosen);
  const [filter, setFilter] = useState('');
  const term = filter.trim().toLowerCase();
  const matches = (model: { id: string; label: string }) =>
    model.id.toLowerCase().includes(term) || model.label.toLowerCase().includes(term);

  // A model chosen before it was withdrawn is still enforced, so it has to stay
  // reachable here rather than disappear on the next save.
  const shown = groups.map((group) => ({ ...group, models: group.models.filter(matches) }));

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((model) => model !== id) : [...current, id]
    );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(T.CATEGORY_MODELS_TITLE, { name: category.name || category.key })}</DialogTitle>
          <DialogDescription>{t(T.CATEGORY_FILTER_MODELS)}</DialogDescription>
        </DialogHeader>
        <Input
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder={t(T.CATEGORY_FILTER_MODELS)}
          aria-label={t(T.CATEGORY_FILTER_MODELS)}
        />
        <div className="flex max-h-80 flex-col gap-3 overflow-y-auto">
          {shown.map((group) => (
            <div key={group.key} className="flex flex-col gap-1">
              <p className="sticky top-0 bg-background py-1 text-xs font-semibold text-muted-foreground">
                {group.label}
              </p>
              {group.models.length === 0 ? (
                <p className="px-2 text-xs text-muted-foreground">
                  {term ? t(T.NOTHING_FOUND) : group.emptyNote}
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {group.models.map((model) => {
                    const owner = categories.find(
                      (other) => other !== category && other.models.includes(model.id)
                    );
                    return (
                      <li key={model.id}>
                        <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-muted">
                          <Checkbox
                            checked={selected.includes(model.id)}
                            onCheckedChange={() => toggle(model.id)}
                          />
                          <span className="min-w-0 truncate font-mono text-xs">{model.label}</span>
                          {owner && (
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {t(T.CATEGORY_ALSO_IN, { name: owner.name || owner.key })}
                            </span>
                          )}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <span className="mr-auto text-xs text-muted-foreground">
            {t(T.CATEGORY_SELECTED_COUNT, { count: selected.length })}
          </span>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t(T.CANCEL)}
          </Button>
          <Button type="button" onClick={() => onApply(selected)}>
            {t(T.APPLY)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
